import axios from 'config/axios';
import { embedAuthEnabled } from './lsgoAuthEmbedSession';

/**
 * Sentral / School Bytes sign-in routing: lsgo-authentication for schools
 * migrated to it, legacy SSO for everyone else. Used by the SIS iframe
 * (routing/guards/SSORoute.js) and the SIS SSO links
 * (/?redirectUri=…&auth_method=sentral_sso|schoolBytes_sso, Login.js),
 * which both carry the SIS school id as ?schoolId=.
 *
 * Legacy's sis/sso/endpoint names the LSGO school and its effective sign-in
 * mode, `authMode: 'legacy' | 'auth'` (lsgo_global.all_schools.authMode,
 * forced to 'legacy' by the global kill switch). Only 'auth' moves a school
 * to lsgo-authentication. Outside production (local, staging) localStorage
 * `lsgo_embed_auth_mode` = 'auth' also sends every school on that browser
 * there, for testing; production ignores it. Anything unknown (no school id,
 * lookup failed, any other authMode) stays on legacy.
 *
 * Flow diagrams and where each piece lives: lsgo-authentication
 * docs/ARCHITECTURE.md, sections 6, 6.1 and 7.2.
 */

const CACHE_PREFIX = 'lsgo_sis_school:';
// Legacy's answer is reused for at most this long, so a cutover or rollback
// (or the kill switch) reaches open tabs within a minute.
const CACHE_TTL_MS = 60 * 1000;

/** ?schoolId= from a query string or a path with one ("/teacher?schoolId=66666_3&"). */
export function sisSchoolIdFrom(pathOrSearch) {
    if (!pathOrSearch) return null;
    const query = pathOrSearch.includes('?') ? pathOrSearch.slice(pathOrSearch.indexOf('?') + 1) : pathOrSearch;
    return new URLSearchParams(query).get('schoolId') || null;
}

async function lookupSchool(sisSchoolId) {
    const key = `${CACHE_PREFIX}${sisSchoolId}`;
    try {
        const cached = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
            return { tenant: cached.tenant, authMode: cached.authMode };
        }
    } catch {
        // Storage unavailable: look it up.
    }
    const { data } = await axios.get(`/api/v4/schools/sis/sso/endpoint/${encodeURIComponent(sisSchoolId)}`);
    const school = {
        tenant: data?.schoolId ? `lsgo_school_${data.schoolId}` : null,
        authMode: data?.authMode ?? 'legacy',
    };
    try {
        if (school.tenant) window.sessionStorage.setItem(key, JSON.stringify({ ...school, fetchedAt: Date.now() }));
    } catch {
        // Storage unavailable: not cached.
    }
    return school;
}

/** { tenant, useLsgoAuth } for an SIS school id. Never throws. */
export async function resolveSisSchool(sisSchoolId) {
    if (!sisSchoolId) return { tenant: null, useLsgoAuth: false };
    try {
        const { tenant, authMode } = await lookupSchool(sisSchoolId);
        return { tenant, useLsgoAuth: Boolean(tenant) && (authMode === 'auth' || embedAuthEnabled()) };
    } catch {
        return { tenant: null, useLsgoAuth: false };
    }
}
